import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { format, startOfDay, subDays } from "date-fns";
import noti from "../assets/noti2Sound.mp3";
import { useTranslation } from "react-i18next"; // Added import

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

// ---------------- Helper ----------------
const pad = (n) => String(n).padStart(2, "0");

function secondsToHMS(sec) {
  const s = sec % 60;
  const m = Math.floor((sec % 3600) / 60);
  const h = Math.floor(sec / 3600);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function secondsToHoursDecimal(sec) {
  return Math.round((sec / 3600) * 100) / 100;
}

function dateKeyFromDate(d) {
  return format(d, "yyyy-MM-dd");
}

// ---------------- Component ----------------
export default function WorkHoursTracker({
  daysToShow = 7,
  listDays = 5,
  theme,
}) {
  const { userId } = useAuth();
  const { t } = useTranslation(); // Added translation hook

  const getUserCollection = (collectionName) => {
    if (!userId) return null;
    return collection(db, "users", userId, collectionName);
  };

  const getUserDoc = (collectionName, docId) => {
    if (!userId || !collectionName || !docId) return null;
    return doc(db, "users", userId, collectionName, docId);
  };

  const [currentSession, setCurrentSession] = useState({
    running: false,
    start: null,
  });
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";

  const lineColor = isDark ? "#f86538" : "#38bdf8";
  const fillColor = isDark
    ? "rgba(11, 161, 226, 0)"
    : "rgba(59, 131, 246, 0.027)";

  const [workHours, setWorkHours] = useState({});
  const soundRef = useRef(null);

  const [tick, setTick] = useState(0);
  const tickRef = useRef(null);
  const [autoPaused, setAutoPaused] = useState(false);

  useEffect(() => {
    soundRef.current = new Audio(noti);
    soundRef.current.volume = 0.7;

    // request notification permission
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  function playSound() {
    if (soundRef.current) {
      soundRef.current.play().catch(() => {});
    }
  }

  function showNotification(title, body) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body });
    }
  }

  // subscribe to workHours collection and current session doc
  useEffect(() => {
    const hoursRef = getUserCollection("workHours");
    if (!hoursRef) return;
    const unsubHours = onSnapshot(hoursRef, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data()?.seconds ?? 0;
      });
      setWorkHours(map);
    });

    const currentRef = getUserDoc("workSessions", "current");
    if (!currentRef) return () => unsubHours();
    const unsubSession = onSnapshot(currentRef, (snapshot) => {
      if (!snapshot.exists()) {
        setCurrentSession({ running: false, start: null });
        setAutoPaused(false);
      } else {
        const data = snapshot.data();
        setCurrentSession({
          running: data.running ?? false,
          start: data.start ?? null,
        });
        setAutoPaused(!!data.autoPaused);
      }
    });

    return () => {
      try {
        unsubHours();
      } catch {}
      try {
        unsubSession();
      } catch {}
    };
  }, [userId]);

  // ticking interval
  useEffect(() => {
    tickRef.current = setInterval(async () => {
      setTick((t) => t + 1);

      // We need to check if we should have auto-paused while the tab was inactive.
      if (currentSession.running && currentSession.start && userId) {
        const now = Date.now();
        const AUTO_PAUSE_MS = 2 * 60 * 60 * 1000; // 2 hours
        const elapsedMs = now - currentSession.start;

        // --- 1. AUTO-PAUSE CHECK (Priority) ---
        // If we have exceeded the 2-hour limit (even if the tab was closed/inactive)
        if (elapsedMs >= AUTO_PAUSE_MS && !autoPaused) {
          console.log(t("workHours.autoPauseLog"));

          // 1. Calculate the 'capped' elapsed time (exactly 2 hours)
          const cappedElapsedSec = Math.floor(AUTO_PAUSE_MS / 1000);

          // 2. Determine where to save this time
          const startKey = dateKeyFromDate(new Date(currentSession.start));

          if (cappedElapsedSec > 0) {
            await _addSecondsToDate(startKey, cappedElapsedSec);
          }

          // 3. Update Firestore to STOP the session
          const sessionDoc = getUserDoc("workSessions", "current");
          if (sessionDoc) {
            await setDoc(sessionDoc, {
              running: false,
              start: null,
              autoPaused: true,
            });
          }

          // 4. Update local state immediately so UI stops counting
          setAutoPaused(true);
          setCurrentSession({ running: false, start: null });

          // 5. Notify
          playSound();
          showNotification(
            t("workHours.autoPauseNotificationTitle"),
            t("workHours.autoPauseNotificationBody")
          );

          // RETURN here so we don't process midnight logic on a session we just stopped.
          return;
        }

        // --- 2. MIDNIGHT BOUNDARY CHECK (Only if NOT auto-paused) ---
        const startDay = dateKeyFromDate(new Date(currentSession.start));
        const currentDay = dateKeyFromDate(new Date(now));

        if (startDay !== currentDay) {
          const midnight = startOfDay(new Date(now)).getTime();

          // Time worked on old day
          const elapsedSecOldDay = Math.floor(
            (midnight - currentSession.start) / 1000
          );

          if (elapsedSecOldDay > 0) {
            await _addSecondsToDate(startDay, elapsedSecOldDay);
          }

          // Reset start to midnight today
          const sessionDoc = getUserDoc("workSessions", "current");
          if (sessionDoc) {
            await setDoc(
              sessionDoc,
              {
                running: true,
                start: midnight,
              },
              { merge: true }
            );
            setCurrentSession((prev) => ({ ...prev, start: midnight }));
          }
        }
      }
    }, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [currentSession.running, currentSession.start, autoPaused, userId, t]);

  // ----------------- helpers -----------------
  async function _addSecondsToDate(dateKey, secondsToAdd) {
    if (!dateKey || !userId || secondsToAdd <= 0) return;
    const ref = getUserDoc("workHours", dateKey);
    if (!ref) return;
    const snap = await getDocSafe(ref);
    const existing = snap?.seconds ?? 0;
    await setDoc(
      ref,
      { seconds: existing + secondsToAdd, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }

  async function getDocSafe(dref) {
    if (!dref) return null;
    try {
      const snap = await getDoc(dref);
      if (!snap.exists()) return null;
      return snap.data();
    } catch (err) {
      console.error(t("workHours.getDocSafeError"), err);
      return null;
    }
  }

  const handleStart = async () => {
    if (!userId) return;
    const now = Date.now();
    const sessionDoc = getUserDoc("workSessions", "current");
    if (!sessionDoc) return;
    await setDoc(sessionDoc, { running: true, start: now });
  };

  const handleStop = async () => {
    if (!currentSession.running || !currentSession.start) {
      const sessionDoc = getUserDoc("workSessions", "current");
      if (sessionDoc) await setDoc(sessionDoc, { running: false, start: null });
      return;
    }
    const now = Date.now();
    const elapsedSec = Math.floor((now - currentSession.start) / 1000);
    const startKey = dateKeyFromDate(new Date(currentSession.start));

    // IMPORTANT: Save time for the day the session started
    await _addSecondsToDate(startKey, elapsedSec);

    const sessionDoc = getUserDoc("workSessions", "current");
    if (sessionDoc) await setDoc(sessionDoc, { running: false, start: null });
  };

  const handleCallItADay = async () => {
    if (currentSession.running && currentSession.start) {
      const now = Date.now();
      const elapsedSec = Math.floor((now - currentSession.start) / 1000);
      const startKey = dateKeyFromDate(new Date(currentSession.start));
      await _addSecondsToDate(startKey, elapsedSec);
    }
    const sessionDoc = getUserDoc("workSessions", "current");
    if (sessionDoc)
      await setDoc(sessionDoc, {
        running: false,
        start: null,
        autoPaused: false,
      });
  };

  const handleResumeAfterAutoPause = async () => {
    if (!userId) return;
    const sessionDoc = getUserDoc("workSessions", "current");
    if (!sessionDoc) return;
    const now = Date.now();
    try {
      await setDoc(sessionDoc, {
        running: true,
        start: now,
        autoPaused: false,
      });
      setAutoPaused(false);
    } catch (err) {
      console.error(t("workHours.resumeError"), err);
    }
  };

  // ----------------- UI calculations -----------------
  const todayKey = dateKeyFromDate(new Date());
  const savedTodaySeconds = workHours[todayKey] ?? 0;
  let displaySeconds = savedTodaySeconds;

  if (currentSession.running && currentSession.start) {
    const sessionStartDate = new Date(currentSession.start);
    const sessionStartDateKey = dateKeyFromDate(sessionStartDate);
    const now = Date.now();

    if (sessionStartDateKey === todayKey) {
      // Session started today: just add elapsed time
      displaySeconds += Math.floor((now - currentSession.start) / 1000);
    } else {
      // Session started on a previous day (or was reset to midnight)
      const midnightTs = startOfDay(new Date()).getTime();

      // Only include time if the session start is *before* now
      if (currentSession.start < now) {
        displaySeconds += Math.floor((now - midnightTs) / 1000);
      }
    }
  }

  // Translate day names for chart
  const getTranslatedDayName = (date) => {
    const dayIndex = date.getDay();
    const days = [
      t("days.sundayShort"),
      t("days.mondayShort"),
      t("days.tuesdayShort"),
      t("days.wednesdayShort"),
      t("days.thursdayShort"),
      t("days.fridayShort"),
      t("days.saturdayShort"),
    ];
    return days[dayIndex];
  };

  const lastNDays = Array.from({ length: daysToShow }).map((_, i) =>
    subDays(new Date(), daysToShow - 1 - i)
  );

  const recentData = lastNDays.map((d) => {
    const k = dateKeyFromDate(d);
    const seconds = k === todayKey ? displaySeconds : workHours[k] ?? 0;
    return { dateKey: k, label: getTranslatedDayName(d), seconds };
  });

  const chartData = {
    labels: recentData.map((d) => d.label),
    datasets: [
      {
        label: t("workHours.chartLabel"),
        data: recentData.map((d) => secondsToHoursDecimal(d.seconds)),
        borderColor: lineColor,
        backgroundColor: fillColor,
        fill: true,
        tension: 0.3,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const gridColor = theme === "dark" ? "#363d52" : "#ddd";
  const maxHours = Math.max(
    ...recentData.map((d) => secondsToHoursDecimal(d.seconds)),
    1
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 },
    scales: {
      y: {
        min: 0,
        max: Math.ceil(maxHours),
        ticks: { callback: (v) => `${v}h` },
        grid: { color: gridColor },
      },
      x: { grid: { color: gridColor } },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y} ${t("workHours.hours")}`,
        },
      },
    },
  };

  const lastListDays = recentData.slice(-listDays).reverse();
  const isRunning = currentSession.running === true;

  return (
    <Card>
      <Header>
        <Title>{t("workHours.title")}</Title>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              width: "80px",
              margin: "10px 0",
            }}
          >
            {format(new Date(), "EEE, MMM d")}
          </div>
        </div>
      </Header>

      <Body>
        <LeftPanel>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Timer>{secondsToHMS(displaySeconds)}</Timer>

            {autoPaused && (
              <AutoPauseOverlayWrapper>
                <AutoPauseOverlay>
                  <AutoPauseText>
                    {t("workHours.autoPauseQuestion")}
                  </AutoPauseText>
                  <AutoPauseSub>{t("workHours.autoPauseMessage")}</AutoPauseSub>
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <PrimaryBtn onClick={handleResumeAfterAutoPause}>
                      {t("workHours.resume")}
                    </PrimaryBtn>
                  </div>
                </AutoPauseOverlay>
              </AutoPauseOverlayWrapper>
            )}

            <Controls style={{ display: "flex", flexDirection: "row" }}>
              {!isRunning ? (
                <PrimaryBtn onClick={handleStart}>
                  {t("workHours.start")}
                </PrimaryBtn>
              ) : (
                <PrimaryBtn danger onClick={handleStop}>
                  {t("workHours.stop")}
                </PrimaryBtn>
              )}
              <SecondaryBtn onClick={handleCallItADay}>
                {t("workHours.callItADay")}
              </SecondaryBtn>
            </Controls>
          </div>

          <div>
            <SmallList>
              {lastListDays.length === 0 && (
                <div style={{ color: "#6b7280" }}>
                  {t("workHours.noRecords")}
                </div>
              )}
              {lastListDays.map((d) => {
                const sec = d.seconds ?? 0;
                const h = Math.floor(sec / 3600);
                const m = Math.floor((sec % 3600) / 60);
                return (
                  <DayItem key={d.dateKey}>
                    <div style={{ fontWeight: 600 }}>{d.label}</div>
                    <div style={{ color: "var(--fontSecondaryColor)" }}>
                      {h}h {m}m
                    </div>
                  </DayItem>
                );
              })}
            </SmallList>
          </div>
        </LeftPanel>

        <RightPanel>
          <div style={{ height: 260, transition: 2 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </RightPanel>
      </Body>
    </Card>
  );
}

// ---------------- Styled ----------------
const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  /* width: 38%; */
  height: 500px;
  flex-direction: column;
  color: var(--fontPrimaryColor);
  transition: 0.5s;
  /* /* @media (max-width: 580px) {
    height: auto;
  } */
  /* @media (max-width: 1850px) {
    width: 48%;
  }
  @media (max-width: 1400px) {
    width: 63%;
  }
  @media (max-width: 1050px) {
    width: 100%;
  }  */
  @media (max-width: 580px) {
    height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  transition: 0.5s;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  width: 100%;
  margin-top: 2em;
  text-align: center;
`;

const Timer = styled.div`
  font-size: 1.4rem;
  font-weight: 400;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
`;

const Controls = styled.div`
  gap: 0.5rem;
  display: flex;
  flex-direction: column-reverse;
`;

const PrimaryBtn = styled.button`
  background: ${({ danger }) => (danger ? "#ef4444" : "#56a835")};
  color: white;
  padding: 0.5rem 0.9rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-family: "Montserrat";
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryBtn = styled.button`
  background: transparent;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: #86161657;
  color: white;
  font-family: "Montserrat";
`;

const Body = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  align-items: flex-start;
  height: 400px;
  @media (max-width: 580px) {
    flex-direction: column;
    height: auto;
  }
`;

// Left panel (timer + list)
const LeftPanel = styled.div`
  width: 40%;
  min-width: 100px;
  height: 400px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2em;
  transition: 0.5s;
  div {
    display: flex;
    flex-direction: column;
  }
  @media (max-width: 580px) {
    height: 220px;
    width: 100%;
    justify-content: space-between;
    align-items: space-between;
    padding: 0 10%;
  }
`;

// Right panel (chart)
const RightPanel = styled.div`
  width: 55%;
  min-width: 300px;
  height: 100px;
  margin-top: 2em;
  @media (max-width: 580px) {
    height: auto;
    width: 100%;
    justify-content: center;
  }
`;

const SmallList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  height: 400px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DayItem = styled.li`
  padding: 0.2rem 0.7rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// ---------------- Updated AutoPause Overlay ----------------
const AutoPauseOverlayWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const AutoPauseOverlay = styled.div`
  background: var(--popupColor);
  color: var(--fontPrimaryColor);
  padding: 24px 32px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const AutoPauseText = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
`;

const AutoPauseSub = styled.div`
  margin-top: 8px;
  opacity: 0.9;
  font-size: 0.95rem;
`;
